import type { ApiService } from '../services/api';
import { fv, type DateRangeValue, type ListFilterState } from '../components/listFilters/listFilterValues';

export const QUOTATION_SORTS = {
  newest: { sortBy: 'createdAt', sortOrder: 'desc' },
  oldest: { sortBy: 'createdAt', sortOrder: 'asc' },
  totalDesc: { sortBy: 'totalPrice', sortOrder: 'desc' },
  totalAsc: { sortBy: 'totalPrice', sortOrder: 'asc' },
  expiringSoon: { sortBy: 'validUntil', sortOrder: 'asc' },
  updated: { sortBy: 'updatedAt', sortOrder: 'desc' },
} as const;

const localDay = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function quotationExpiringRange(now = new Date()): DateRangeValue {
  const end = new Date(now);
  end.setDate(end.getDate() + 7);
  return { from: localDay(now), to: localDay(end) };
}

/** Filter dates describe calendar days in the user's timezone, including the entire end day. */
function dayBoundary(value?: string, end = false): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime()) || localDay(date) !== value) return undefined;
  if (end) date.setHours(23, 59, 59, 999);
  return date;
}

type QuotationListQuery = NonNullable<Parameters<ApiService['getQuotations']>[0]>;

export function buildQuotationListQuery(state: Pick<ListFilterState, 'filters' | 'sort' | 'search'>): QuotationListQuery {
  const f = state.filters;
  const sort = QUOTATION_SORTS[state.sort as keyof typeof QUOTATION_SORTS] || QUOTATION_SORTS.newest;
  return {
    search: fv.text(state.search),
    statuses: fv.list(f.statuses),
    createdFrom: dayBoundary(f.createdAt?.from), createdTo: dayBoundary(f.createdAt?.to, true),
    validFrom: dayBoundary(f.validUntil?.from), validTo: dayBoundary(f.validUntil?.to, true),
    totalMin: fv.min(f.total), totalMax: fv.max(f.total),
    validity: f.validity, converted: f.converted,
    createdBy: fv.text(f.createdBy), currency: fv.text(f.currency)?.toUpperCase(),
    productIds: fv.csv(f.products), vendorIds: fv.csv(f.vendors),
    ...sort,
  };
}
